import React, { useEffect, useState, useCallback } from "react";
import api from "../../api/axiosInstance";
import { FaFilePdf } from "react-icons/fa";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Box, Typography, Pagination, MenuItem, FormControl, Select, InputLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Skeleton } from "@mui/material";
import backgroundPattern from '../../assets/pasbookpng.png';

const VendorProductDetails = () => {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  
  const vendorId = params.vendorId;
  const goodsItemId = params.goodsItemId ? parseInt(params.goodsItemId) : null;
  
  // Get date filters from URL parameters
  const fromDate = searchParams.get('fromDate');
  const toDate = searchParams.get('toDate');
  
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [vendorName, setVendorName] = useState("");
  const [productName, setProductName] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [backgroundSize, setBackgroundSize] = useState('220px');

  // Responsive background size
  useEffect(() => {
    const updateBackgroundSize = () => {
      if (window.innerWidth <= 480) {
        setBackgroundSize('120px');
      } else if (window.innerWidth <= 768) {
        setBackgroundSize('150px');
      } else {
        setBackgroundSize('220px');
      }
    };

    updateBackgroundSize();
    window.addEventListener('resize', updateBackgroundSize);
    return () => window.removeEventListener('resize', updateBackgroundSize);
  }, []);

  const fetchProductDetails = useCallback(async () => {
    if (!vendorId || !goodsItemId) {
      setError("Missing vendor ID or product ID");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const requestParams = {
        page,
        size: pageSize
      };

      // Add date filters if they exist
      if (fromDate) requestParams.fromDate = fromDate;
      if (toDate) requestParams.toDate = toDate;

      const response = await api.get(
        `purchase/statement/vendor/${vendorId}/product/${goodsItemId}`,
        {
          params: requestParams
        }
      );

      const data = response.data || [];
      setRecords(data);
      setTotalPages(data.totalPages || 0);
      setTotalElements(data.totalElements || 0);
      
      // Set vendor name and product name from first record if available
      if (data.length > 0 && data[0].vendorName) {
        setVendorName(data[0].vendorName);
      }
      if (data.length > 0 && data[0].productName) {
        setProductName(data[0].productName);
      }

    } catch (err) {
      console.error("Error fetching vendor product details:", err);
      setError(err.response?.data?.message || "Failed to fetch product details");
      toast.error("Failed to load product details");
    } finally {
      setLoading(false);
    }
  }, [vendorId, goodsItemId, page, pageSize, fromDate, toDate]);

  useEffect(() => {
    fetchProductDetails();
  }, [fetchProductDetails]);

  const handleExportAllPDF = async () => {
    try {
      const requestParams = {
        vendorId,
        goodsItemId
      };

      // Add date filters if they exist
      if (fromDate) requestParams.fromDate = fromDate;
      if (toDate) requestParams.toDate = toDate;

      const response = await api.get(`purchase/statement/vendor/${vendorId}/product/${goodsItemId}/pdf`, {
        params: requestParams,
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vendor_product_details_${productName}_${vendorName}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success("Product details exported successfully");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      if (error.response?.status === 404) {
        toast.error("PDF export not available for vendor products yet");
      } else {
        toast.error("Failed to export product details");
      }
    }
  };

  const handleBack = () => {
    // Preserve filters using URL parameters when returning to Passbook
    const params = new URLSearchParams();
    if (vendorId) params.append('vendorId', vendorId);
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);
    
    const queryString = params.toString();
    const url = queryString 
      ? `/dashboard/purchases/vendor-passbook?${queryString}`
      : `/dashboard/purchases/vendor-passbook`;
    
    navigate(url);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage - 1); // Convert 1-based to 0-based indexing
  };

  const handlePageSizeChange = (e) => {
    setPageSize(e.target.value);
    setPage(0);
  };

  return (
    <div style={{ position: 'relative', padding: '24px', minHeight: '100vh', background: '#f6f8fb' }}>
      <div
        style={{
          position: 'fixed',
          inset: '-30%',
          backgroundImage: `url(${backgroundPattern})`,
          backgroundRepeat: 'repeat',
          backgroundSize: backgroundSize,
          transform: 'rotate(-12deg)',
          opacity: 0.08,
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      <div style={{ position: 'relative', zIndex: 1 }}>
      {/* Page Header */}
      <div style={{ 
        marginBottom: '24px'
      }}>
        <div style={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
          marginBottom: '16px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button 
              type="button"
              onClick={handleBack}
              style={{
                width: '40px',
                height: '40px',
                border: 'none',
                borderRadius: '10px',
                background: '#f8fafc',
                color: '#64748b',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.25s ease',
                flexShrink: 0
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#eef2ff';
                e.target.style.color = '#4338ca';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#f8fafc';
                e.target.style.color = '#64748b';
              }}
            >
              <svg stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" height="20" width="20" xmlns="http://www.w3.org/2000/svg">
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
            </button>
            <div>
              <h3 style={{ 
                margin: '0 0 4px 0',
                fontSize: '18px',
                fontWeight: '600',
                color: '#1f2937'
              }}>
                Product Details
              </h3>
              <p style={{ 
                margin: '0',
                fontSize: '14px',
                color: '#6b7280'
              }}>
                {vendorName || 'Vendor'} - {productName}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button
              onClick={handleExportAllPDF}
              style={{
                padding: '8px 16px',
                border: '1px solid #3b82f6',
                borderRadius: '8px',
                backgroundColor: '#3b82f6',
                color: '#ffffff',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#2563eb';
                e.target.style.borderColor = '#2563eb';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#3b82f6';
                e.target.style.borderColor = '#3b82f6';
              }}
            >
              <FaFilePdf /> Export PDF
            </button>
          </div>
        </div>
      </div>

      {/* Transaction Details Card */}
      <div style={{ 
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        padding: '20px',
        marginBottom: '20px',
        boxSizing: 'border-box'
      }}>
        {loading ? (
          <div style={{ 
            padding: '60px 20px',
            textAlign: 'center',
            color: '#6b7280'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <p>Loading transactions...</p>
          </div>
        ) : error ? (
          <div style={{ 
            padding: '60px 20px',
            textAlign: 'center',
            color: '#ef4444'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>❌</div>
            <p>{error}</p>
          </div>
        ) : (
          <TableContainer sx={{ 
            maxWidth: '100%',
            overflowX: 'auto',
            overflowY: 'auto',
            "&::-webkit-scrollbar": {
              width: 8,
              height: 8,
            },
            "&::-webkit-scrollbar-thumb": {
              borderRadius: 20,
              background: "#cbd5e1",
            },
          }}>
            <Table sx={{ 
              minWidth: { xs: 1200, md: '100%' },
              width: '100%'
            }}>
              <TableHead sx={{
                '& th': { position: 'sticky', top: 0, zIndex: 5, background: 'white' }
              }}>
                <TableRow>
                  <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: '#F8FAFC', fontSize: 13, padding: { xs: '12px 10px', md: '16px' } }}>
                    Purchase No.
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: '#F8FAFC', fontSize: 13, padding: { xs: '12px 10px', md: '16px' } }}>
                    Date
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: '#F8FAFC', fontSize: 13, padding: { xs: '12px 10px', md: '16px' } }}>
                    Type
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: '#F8FAFC', fontSize: 13, padding: { xs: '12px 10px', md: '16px' } }}>
                    Opening Bal
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: '#F8FAFC', fontSize: 13, padding: { xs: '12px 10px', md: '16px' } }}>
                    Inward Qty
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: '#F8FAFC', fontSize: 13, padding: { xs: '12px 10px', md: '16px' } }}>
                    Outward Qty
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: '#F8FAFC', fontSize: 13, padding: { xs: '12px 10px', md: '16px' } }}>
                    Closing Bal
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  [...Array(pageSize)].map((_, index) => (
                    <TableRow key={index} sx={{ height: 64 }}>
                      <TableCell align="center"><Skeleton variant="text" width={100} /></TableCell>
                      <TableCell align="center"><Skeleton variant="text" width={80} /></TableCell>
                      <TableCell align="center"><Skeleton variant="text" width={80} /></TableCell>
                      <TableCell align="center"><Skeleton variant="text" width={80} /></TableCell>
                      <TableCell align="center"><Skeleton variant="text" width={80} /></TableCell>
                      <TableCell align="center"><Skeleton variant="text" width={80} /></TableCell>
                      <TableCell align="center"><Skeleton variant="text" width={80} /></TableCell>
                    </TableRow>
                  ))
                ) : records.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ fontSize: 48, opacity: 0.5 }}>📋</Box>
                        <Typography variant="h6" color="text.primary" fontWeight={600}>
                          No Transactions Found
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  records.map((record) => (
                    <TableRow key={record.id} sx={{ '&:hover': { backgroundColor: '#F8FAFC' } }}>
                      <TableCell align="center" sx={{ color: '#1e293b', fontWeight: 500 }}>
                        {record.purchaseNumber}
                      </TableCell>
                      <TableCell align="center" sx={{ color: '#64748b' }}>
                        {new Date(record.purchaseDate).toLocaleDateString('en-IN', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        })}
                      </TableCell>
                      <TableCell align="center" sx={{ color: '#64748b' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '600',
                          ...(record.purchaseType === 'INWARD' && { background: '#DCFCE7', color: '#15803D' }),
                          ...(record.purchaseType === 'OUTWARD' && { background: '#DBEAFE', color: '#2563EB' }),
                        }}>
                          {record.purchaseType}
                        </span>
                      </TableCell>
                      <TableCell align="center" sx={{ color: '#64748b' }}>
                        {record.openingBalance}
                      </TableCell>
                      <TableCell align="center" sx={{ color: '#64748b' }}>
                        {record.inwardQty || 0}
                      </TableCell>
                      <TableCell align="center" sx={{ color: '#64748b' }}>
                        {record.outwardQty || 0}
                      </TableCell>
                      <TableCell align="center" sx={{ 
                        color: record.closingBalance >= 0 ? '#059669' : '#dc2626',
                        fontWeight: 600
                      }}>
                        {record.closingBalance ?? 0}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer> 
        )}

        {/* Pagination */}
        {!loading && !error && totalPages > 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: {
                xs: "column",
                md: "row",
              },
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              p: 2,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: { xs: "center", md: "flex-start" }, gap: 1 }}>
              {totalElements > 0 && (
                <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
                  Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, totalElements)} of {totalElements} records
                </Typography>
              )}
              <Pagination
                count={totalPages}
                page={page + 1}
                onChange={handlePageChange}
                disabled={loading}
                shape="rounded"
                variant="outlined"
                color="primary"
                showFirstButton
                showLastButton
                siblingCount={0}
                boundaryCount={1}
                size="small"
                sx={{
                  "& .MuiPaginationItem-root": {
                    transition: "transform .2s, box-shadow .2s, background-color .2s",
                    borderRadius: "10px",
                    fontWeight: 600,
                    minWidth: 38,
                    height: 38,
                  },
                  "& .MuiPaginationItem-root:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 10px rgba(0,0,0,.15)",
                  },
                  "& .MuiPaginationItem-root:active": {
                    transform: "scale(.95)",
                  },
                  "& .Mui-selected": {
                    backgroundColor: "#4338CA",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "#3730a3",
                    },
                  },
                }}
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: "#475569", mb: 0.75 }}>Rows per page</Typography>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Rows</InputLabel>
                <Select
                  value={pageSize}
                  label="Rows"
                  onChange={handlePageSizeChange}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        )}
      </div>
      </div>
    </div>
  );
};

export default VendorProductDetails;